import { useState, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, Users, ShieldCheck, Briefcase, Calculator,
  KeyRound, Shield, Lock, Mail, Camera, UserCircle2, Eye, EyeOff, Copy,
  ShieldPlus, CheckCircle2, XCircle,
} from "lucide-react";

// ─── Tipos e constantes ───────────────────────────────────────────────────────

const ROLE_LABELS_PERM: Record<string, string> = {
  admin: "Administrador",
  recepcionista: "Recepcionista",
  funcionario: "Profissional",
  caixa: "Caixa",
};

function StaffPermissions() {
  const [selectedStaffId, setSelectedStaffId] = useState<number | null>(null);
  const [localPerms, setLocalPerms] = useState<Record<string, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const { data: staffList, isLoading: loadingList } = (trpc as any).staffPerms.listWithPermissions.useQuery();
  const { data: staffPerms, isLoading: loadingPerms } = (trpc as any).staffPerms.getForStaff.useQuery(
    { staffId: selectedStaffId! },
    { enabled: !!selectedStaffId }
  );
  const utils = trpc.useUtils();
  const saveMutation = (trpc as any).staffPerms.setForStaff.useMutation({
    onSuccess: () => {
      toast.success("Permissões salvas com sucesso!");
      setHasChanges(false);
      utils.invalidate();
    },
    onError: (err: any) => toast.error(err.message ?? "Erro ao salvar permissões"),
  });

  useEffect(() => {
    if (staffPerms?.modules) {
      const map: Record<string, boolean> = {};
      for (const m of staffPerms.modules) {
        map[m.key] = m.hasAccess;
      }
      setLocalPerms(map);
      setHasChanges(false);
    }
  }, [staffPerms]);

  const handleToggle = (moduleKey: string, value: boolean) => {
    setLocalPerms(prev => ({ ...prev, [moduleKey]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!selectedStaffId) return;
    const permissions = Object.entries(localPerms).map(([module, granted]) => ({ module, granted }));
    saveMutation.mutate({ staffId: selectedStaffId, permissions });
  };

  const handleReset = () => {
    if (staffPerms?.modules) {
      const map: Record<string, boolean> = {};
      for (const m of staffPerms.modules) { map[m.key] = m.hasAccess; }
      setLocalPerms(map);
      setHasChanges(false);
    }
  };

  const selectedStaff = (staffList ?? []).find((s: any) => s.id === selectedStaffId);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldPlus className="w-4 h-4 text-primary" />
            Selecionar Funcionário
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingList ? (
            <div className="text-sm text-muted-foreground">Carregando equipe...</div>
          ) : (
            <Select
              value={selectedStaffId?.toString() ?? ""}
              onValueChange={v => { setSelectedStaffId(Number(v)); setHasChanges(false); }}
            >
              <SelectTrigger className="w-full max-w-sm">
                <SelectValue placeholder="Escolha um funcionário..." />
              </SelectTrigger>
              <SelectContent>
                {(staffList ?? []).map((s: any) => (
                  <SelectItem key={s.id} value={s.id.toString()}>
                    {s.name} — {ROLE_LABELS_PERM[s.staffRole] ?? s.staffRole}
                    {s.overridesCount > 0 && ` (${s.overridesCount} override${s.overridesCount > 1 ? "s" : ""})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {selectedStaffId && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base">Permissões de {(selectedStaff as any)?.name ?? "..."}</CardTitle>
              {staffPerms && (
                <p className="text-xs text-muted-foreground mt-1">
                  Cargo base: <strong>{ROLE_LABELS_PERM[staffPerms.staffRole] ?? staffPerms.staffRole}</strong>
                  {" — "}
                  <span className="text-blue-500">Azul</span> = padrão do cargo · <span className="text-amber-500">Âmbar</span> = override ativo
                </p>
              )}
            </div>
            {hasChanges && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleReset}>Cancelar</Button>
                <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Salvando..." : "Salvar Permissões"}
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {loadingPerms ? (
              <div className="text-sm text-muted-foreground">Carregando permissões...</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(staffPerms?.modules ?? []).map((m: any) => {
                  const current = localPerms[m.key] ?? m.hasAccess;
                  const isOverride = current !== m.isDefault;
                  return (
                    <div key={m.key} className={`flex items-center justify-between rounded-lg border p-3 transition-colors ${
                      isOverride ? "border-amber-400 bg-amber-50 dark:bg-amber-950/20" :
                      current ? "border-blue-200 bg-blue-50/50 dark:bg-blue-950/20" :
                      "border-border bg-muted/30"
                    }`}>
                      <div className="flex items-center gap-2">
                        {current ? (
                          <CheckCircle2 className={`w-4 h-4 ${isOverride ? "text-amber-500" : "text-blue-500"}`} />
                        ) : (
                          <XCircle className="w-4 h-4 text-muted-foreground" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{m.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {m.isDefault ? "Padrão do cargo" : "Fora do cargo"}
                            {isOverride && " · Override ativo"}
                          </p>
                        </div>
                      </div>
                      <Switch checked={current} onCheckedChange={v => handleToggle(m.key, v)} />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!selectedStaffId && (
        <div className="text-center py-12 text-muted-foreground">
          <ShieldPlus className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Selecione um funcionário acima para gerenciar suas permissões individuais.</p>
        </div>
      )}
    </div>
  );
}

type StaffRole = "admin" | "funcionario" | "caixa" | "recepcionista";
type TeamRole = "professional" | "receptionist" | "manager" | "admin";

const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  admin: "Administrador",
  funcionario: "Funcionário",
  caixa: "Caixa",
  recepcionista: "Recepcionista",
};
const STAFF_ROLE_COLORS: Record<StaffRole, string> = {
  admin: "bg-violet-100 text-violet-800 border-violet-200",
  funcionario: "bg-blue-100 text-blue-800 border-blue-200",
  caixa: "bg-amber-100 text-amber-800 border-amber-200",
  recepcionista: "bg-green-100 text-green-800 border-green-200",
};
const STAFF_ROLE_ICONS: Record<StaffRole, React.ReactNode> = {
  admin: <ShieldCheck className="w-3 h-3" />,
  funcionario: <Briefcase className="w-3 h-3" />,
  caixa: <Calculator className="w-3 h-3" />,
  recepcionista: <Users className="w-3 h-3" />,
};
const STAFF_ROLE_DESCRIPTIONS: Record<StaffRole, string> = {
  admin: "Acesso total: configurações, relatórios, equipe e agendamentos",
  funcionario: "Pode criar agendamentos, walk-in e ver sua agenda",
  caixa: "Acesso ao caixa, pagamentos e agendamentos do dia",
  recepcionista: "Agenda semanal de todos + criar/editar agendamentos + caixa",
};

const TEAM_ROLE_LABELS: Record<TeamRole, string> = {
  professional: "Profissional",
  receptionist: "Recepcionista",
  manager: "Gerente",
  admin: "Admin",
};
const TEAM_ROLE_COLORS: Record<TeamRole, string> = {
  professional: "bg-blue-100 text-blue-700",
  receptionist: "bg-green-100 text-green-700",
  manager: "bg-purple-100 text-purple-700",
  admin: "bg-amber-100 text-amber-700",
};
const TEAM_ROLE_DESCRIPTIONS: Record<TeamRole, string> = {
  professional: "Vê só a própria agenda e comissões",
  receptionist: "Agenda de todos + criar/editar agendamentos + caixa",
  manager: "Caixa + relatórios + tudo da recepcionista",
  admin: "Acesso total",
};

// ─── Modal de PIN ─────────────────────────────────────────────────────────────
function SetPinModal({
  open, onClose, staffId, staffName,
}: { open: boolean; onClose: () => void; staffId?: number; staffName?: string }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const utils = trpc.useUtils();

  const setPin_ = trpc.authorization.setPin.useMutation({
    onSuccess: () => {
      toast.success(staffId ? `PIN definido para ${staffName}!` : "Seu PIN foi definido com sucesso!");
      utils.staff.list.invalidate();
      setPin(""); setConfirmPin(""); onClose();
    },
    onError: (e) => toast.error("Erro ao definir PIN", { description: e.message }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length < 4) { toast.error("O PIN deve ter pelo menos 4 dígitos."); return; }
    if (!/^\d+$/.test(pin)) { toast.error("O PIN deve conter apenas números."); return; }
    if (pin !== confirmPin) { toast.error("Os PINs não coincidem."); return; }
    setPin_.mutate({ pin, staffId });
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            <DialogTitle>
              {staffId ? `Definir PIN para ${staffName}` : "Definir Meu PIN de Autorização"}
            </DialogTitle>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <p className="text-sm text-muted-foreground">
            {staffId
              ? `Este PIN será usado por ${staffName} para autorizar cancelamentos e estornos.`
              : "Este PIN será solicitado ao cancelar agendamentos ou estornar pagamentos."}
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Novo PIN (4–6 dígitos)</Label>
            <Input type="password" inputMode="numeric" placeholder="••••"
              value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Confirmar PIN</Label>
            <Input type="password" inputMode="numeric" placeholder="••••"
              value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))} />
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={setPin_.isPending}>
              {setPin_.isPending ? "Salvando..." : "Definir PIN"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Aba 1: Acesso por E-mail (sistema antigo OAuth) ─────────────────────────
function StaffByEmail() {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState<{ name: string; email: string; phone: string; staffRole: StaffRole }>({
    name: "", email: "", phone: "", staffRole: "funcionario",
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [photoMime, setPhotoMime] = useState<"image/jpeg" | "image/png" | "image/webp">("image/jpeg");
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinTargetId, setPinTargetId] = useState<number | undefined>(undefined);
  const [pinTargetName, setPinTargetName] = useState<string | undefined>(undefined);

  const { data: staffList, isLoading } = trpc.staff.list.useQuery();
  const { data: myAuthInfo } = trpc.authorization.canAuthorize.useQuery();
  const { data: profList = [] } = trpc.professionals.listAdmin.useQuery();
  const utils = trpc.useUtils();

  const linkProfessional = (trpc as any).staff.linkProfessional.useMutation({
    onSuccess: () => { toast.success("Vínculo atualizado!"); utils.staff.list.invalidate(); },
    onError: (err: any) => toast.error("Erro ao vincular", { description: err.message }),
  });
  const createStaff = trpc.staff.create.useMutation({
    onSuccess: () => { toast.success("Membro adicionado!"); utils.staff.list.invalidate(); handleCloseForm(); },
    onError: (err) => toast.error("Erro ao adicionar", { description: err.message }),
  });
  const updateStaff = trpc.staff.update.useMutation({
    onSuccess: () => { toast.success("Membro atualizado!"); utils.staff.list.invalidate(); handleCloseForm(); },
    onError: (err) => toast.error("Erro ao atualizar", { description: err.message }),
  });
  const deleteStaff = trpc.staff.delete.useMutation({
    onSuccess: () => { toast.success("Membro removido."); utils.staff.list.invalidate(); setDeleteId(null); },
    onError: (err) => toast.error("Erro ao remover", { description: err.message }),
  });
  const resendInvite = trpc.staff.resendInvite.useMutation({
    onSuccess: (data) => toast.success("Convite reenviado!", { description: `E-mail enviado para ${data.email}` }),
    onError: (err) => toast.error("Erro ao reenviar", { description: err.message }),
  });
  const toggleCanAuthorize = trpc.authorization.toggleCanAuthorize.useMutation({
    onSuccess: (_, vars) => {
      toast.success(vars.canAuthorize ? "Permissão concedida!" : "Permissão revogada.");
      utils.staff.list.invalidate();
    },
    onError: (err) => toast.error("Erro ao alterar permissão", { description: err.message }),
  });
  const uploadPhoto = (trpc as any).staff.uploadPhoto.useMutation({
    onSuccess: (data: any) => { toast.success("Foto atualizada!"); utils.staff.list.invalidate(); setPhotoPreview(data.photoUrl); setPhotoBase64(null); },
    onError: (err: any) => toast.error("Erro ao enviar foto", { description: err.message }),
  });

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Foto muito grande. Máximo 5MB."); return; }
    setPhotoMime(file.type as any);
    const reader = new FileReader();
    reader.onload = (ev) => { const b64 = ev.target?.result as string; setPhotoBase64(b64); setPhotoPreview(b64); };
    reader.readAsDataURL(file);
  }

  function handleOpenCreate() {
    setForm({ name: "", email: "", phone: "", staffRole: "funcionario" });
    setEditingId(null); setPhotoPreview(null); setPhotoBase64(null); setShowForm(true);
  }
  function handleOpenEdit(member: any) {
    setForm({ name: member.name, email: member.email ?? "", phone: member.phone ?? "", staffRole: member.staffRole });
    setEditingId(member.id); setPhotoPreview(member.photoUrl ?? null); setPhotoBase64(null); setShowForm(true);
  }
  function handleCloseForm() {
    setShowForm(false); setEditingId(null);
    setForm({ name: "", email: "", phone: "", staffRole: "funcionario" });
    setPhotoPreview(null); setPhotoBase64(null);
  }
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Informe o nome do membro"); return; }
    if (editingId) {
      updateStaff.mutate({ id: editingId, ...form });
      if (photoBase64) uploadPhoto.mutate({ staffId: editingId, base64: photoBase64, mimeType: photoMime });
    } else {
      createStaff.mutate(form, {
        onSuccess: (data: any) => { if (photoBase64 && data?.id) uploadPhoto.mutate({ staffId: data.id, base64: photoBase64, mimeType: photoMime }); },
      });
    }
  }

  const isPending = createStaff.isPending || updateStaff.isPending;

  return (
    <div className="space-y-5">
      {/* PIN do administrador */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-600" />
            PIN de Autorização (Administrador)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            O PIN é exigido para <strong>cancelar agendamentos</strong> e <strong>estornar pagamentos</strong> no caixa.
          </p>
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${myAuthInfo?.hasPin ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              <Lock className="w-3 h-3" />
              {myAuthInfo?.hasPin ? "PIN configurado" : "PIN não configurado"}
            </div>
            <Button size="sm" variant="outline" onClick={() => { setPinTargetId(undefined); setPinTargetName(undefined); setPinModalOpen(true); }} className="gap-1.5">
              <KeyRound className="w-3.5 h-3.5" />
              {myAuthInfo?.hasPin ? "Alterar meu PIN" : "Definir meu PIN"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Legenda de roles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(["admin", "funcionario", "caixa"] as StaffRole[]).map(role => (
          <div key={role} className="rounded-lg border border-border/60 bg-card p-3 space-y-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${STAFF_ROLE_COLORS[role]}`}>
              {STAFF_ROLE_ICONS[role]}{STAFF_ROLE_LABELS[role]}
            </span>
            <p className="text-xs text-muted-foreground">{STAFF_ROLE_DESCRIPTIONS[role]}</p>
          </div>
        ))}
      </div>

      {/* Botão adicionar */}
      <div className="flex justify-end">
        <Button onClick={handleOpenCreate} className="gap-2 w-full sm:w-auto">
          <Plus className="w-4 h-4" />Adicionar Membro
        </Button>
      </div>

      {/* Cartões — mobile */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Carregando equipe...</CardContent></Card>
        ) : !staffList?.length ? (
          <Card>
            <CardContent className="py-10 text-center">
              <Users className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Nenhum membro cadastrado ainda.</p>
            </CardContent>
          </Card>
        ) : (
          staffList.map(member => (
            <Card key={member.id} className="border-border/70">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  {(member as any).photoUrl ? (
                    <img src={(member as any).photoUrl} alt={member.name} className="w-10 h-10 rounded-full object-cover border border-border shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center border border-dashed border-border shrink-0">
                      <UserCircle2 className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{member.name}</p>
                    {member.email && <p className="text-xs text-muted-foreground truncate">{member.email}</p>}
                    {member.phone && <p className="text-xs text-muted-foreground">{member.phone}</p>}
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${STAFF_ROLE_COLORS[member.staffRole as StaffRole]}`}>
                    {STAFF_ROLE_ICONS[member.staffRole as StaffRole]}{STAFF_ROLE_LABELS[member.staffRole as StaffRole]}
                  </span>
                </div>

                {member.staffRole !== "admin" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Profissional vinculado</Label>
                    <Select
                      value={String((member as any).professionalId ?? "none")}
                      onValueChange={(v) => linkProfessional.mutate({ staffId: member.id, professionalId: v === "none" ? null : Number(v) })}
                    >
                      <SelectTrigger className="w-full h-9 text-xs"><SelectValue placeholder="Sem vínculo" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem vínculo</SelectItem>
                        {(profList as any[]).map((p: any) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium">Pode autorizar operações</p>
                    <p className="text-[11px] text-muted-foreground">{member.staffRole === "admin" ? "Sempre disponível para administradores" : "Cancelar agendamentos e estornar pagamentos"}</p>
                  </div>
                  {member.staffRole === "admin" ? (
                    <Badge className="text-xs bg-violet-100 text-violet-700 border-violet-200 shrink-0">Admin</Badge>
                  ) : (
                    <Switch
                      checked={!!(member as any).canAuthorize}
                      onCheckedChange={(checked) => toggleCanAuthorize.mutate({ staffId: member.id, canAuthorize: checked })}
                      disabled={toggleCanAuthorize.isPending}
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => handleOpenEdit(member)}>
                    <Pencil className="w-3.5 h-3.5" />Editar
                  </Button>
                  <Button variant="outline" size="sm" className="w-full gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setDeleteId(member.id)}>
                    <Trash2 className="w-3.5 h-3.5" />Excluir
                  </Button>
                </div>
                {member.email && (
                  <Button variant="ghost" size="sm" className="w-full gap-1.5 text-blue-600" onClick={() => resendInvite.mutate({ staffId: member.id })} disabled={resendInvite.isPending}>
                    <Mail className="w-3.5 h-3.5" />Reenviar acesso por e-mail
                  </Button>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Tabela */}
      <div className="hidden md:block rounded-lg border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Nome</TableHead>
              <TableHead>Contato</TableHead>
              <TableHead>Nível de Acesso</TableHead>
              <TableHead>Profissional Vinculado</TableHead>
              <TableHead><div className="flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-amber-600" />Pode Autorizar</div></TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Carregando equipe...</TableCell></TableRow>
            ) : !staffList?.length ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Users className="w-8 h-8 opacity-30" />
                    <p className="text-sm">Nenhum membro cadastrado ainda.</p>
                    <Button variant="outline" size="sm" onClick={handleOpenCreate} className="mt-1 gap-1">
                      <Plus className="w-3 h-3" />Adicionar primeiro membro
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              staffList.map(member => (
                <TableRow key={member.id} className="hover:bg-muted/30">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2.5">
                      <div className="relative group/avatar shrink-0 cursor-pointer" onClick={() => handleOpenEdit(member)}>
                        {(member as any).photoUrl ? (
                          <img src={(member as any).photoUrl} alt={member.name} className="w-8 h-8 rounded-full object-cover border border-border" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center border border-dashed border-border">
                            <UserCircle2 className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover/avatar:opacity-100 flex items-center justify-center transition-opacity">
                          <Camera className="w-3 h-3 text-white" />
                        </div>
                      </div>
                      <span>{member.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      {member.email && <p className="text-sm">{member.email}</p>}
                      {member.phone && <p className="text-xs text-muted-foreground">{member.phone}</p>}
                      {!member.email && !member.phone && <span className="text-xs text-muted-foreground italic">Sem contato</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${STAFF_ROLE_COLORS[member.staffRole as StaffRole]}`}>
                      {STAFF_ROLE_ICONS[member.staffRole as StaffRole]}{STAFF_ROLE_LABELS[member.staffRole as StaffRole]}
                    </span>
                  </TableCell>
                  <TableCell>
                    {member.staffRole === "admin" ? (
                      <span className="text-xs text-muted-foreground italic">N/A</span>
                    ) : (
                      <Select value={String((member as any).professionalId ?? "none")}
                        onValueChange={(v) => linkProfessional.mutate({ staffId: member.id, professionalId: v === "none" ? null : Number(v) })}>
                        <SelectTrigger className="h-7 text-xs w-36"><SelectValue placeholder="Sem vínculo" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem vínculo</SelectItem>
                          {(profList as any[]).map((p: any) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    {member.staffRole === "admin" ? (
                      <Badge className="text-xs bg-violet-100 text-violet-700 border-violet-200">Sempre (admin)</Badge>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Switch checked={!!(member as any).canAuthorize}
                          onCheckedChange={(checked) => toggleCanAuthorize.mutate({ staffId: member.id, canAuthorize: checked })}
                          disabled={toggleCanAuthorize.isPending} />
                        {(member as any).canAuthorize && (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => { setPinTargetId(member.id); setPinTargetName(member.name); setPinModalOpen(true); }}>
                            <KeyRound className="w-3 h-3 mr-1" />
                            {(member as any).authPin ? "Alterar PIN" : "Definir PIN"}
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenEdit(member)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      {member.email && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500 hover:text-blue-600"
                          title="Reenviar acesso por e-mail" onClick={() => resendInvite.mutate({ staffId: member.id })} disabled={resendInvite.isPending}>
                          <Mail className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleteId(member.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modal criar/editar */}
      <Dialog open={showForm} onOpenChange={v => !v && handleCloseForm()}>
        <DialogContent className="sm:max-w-md max-h-[90dvh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>{editingId ? "Editar Membro" : "Adicionar Membro à Equipe"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto overscroll-contain space-y-4 pr-1">
              <div className="flex flex-col items-center gap-2">
                <div className="relative group/photo cursor-pointer" onClick={() => photoInputRef.current?.click()}>
                  {photoPreview ? (
                    <img src={photoPreview} alt="Foto" className="w-20 h-20 rounded-full object-cover border-2 border-border" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                      <UserCircle2 className="w-10 h-10 text-muted-foreground" />
                    </div>
                  )}
                  <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover/photo:opacity-100 flex items-center justify-center transition-opacity">
                    <Camera className="w-5 h-5 text-white" />
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">Clique para adicionar foto (máx. 5MB)</span>
                <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoChange} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Nome *</Label>
                <Input placeholder="Nome completo" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">E-mail</Label>
                  <Input type="email" placeholder="email@exemplo.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Telefone</Label>
                  <Input placeholder="(11) 99999-9999" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Nível de Acesso *</Label>
                <Select value={form.staffRole} onValueChange={v => setForm(f => ({ ...f, staffRole: v as StaffRole }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(["admin", "funcionario", "caixa", "recepcionista"] as StaffRole[]).map(role => (
                      <SelectItem key={role} value={role}>
                        <div className="flex flex-col">
                          <span className="font-medium">{STAFF_ROLE_LABELS[role]}</span>
                          <span className="text-xs text-muted-foreground">{STAFF_ROLE_DESCRIPTIONS[role]}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-md bg-muted/50 border border-border/60 p-3 text-xs text-muted-foreground">
                <strong className="text-foreground">Como funciona:</strong> após cadastrar, o membro faz login com o mesmo e-mail informado. O acesso é vinculado automaticamente ao salão.
              </div>
            </div>
            <DialogFooter className="shrink-0 pt-4 border-t mt-4 gap-2">
              <Button type="button" variant="outline" onClick={handleCloseForm} disabled={isPending}>Cancelar</Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Salvando..." : editingId ? "Salvar Alterações" : "Adicionar Membro"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={v => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover membro da equipe?</AlertDialogTitle>
            <AlertDialogDescription>O membro perderá o acesso imediatamente. Pode ser revertido adicionando-o novamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteStaff.mutate({ id: deleteId })} disabled={deleteStaff.isPending}>
              {deleteStaff.isPending ? "Removendo..." : "Remover"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de PIN */}
      <SetPinModal open={pinModalOpen} onClose={() => { setPinModalOpen(false); setPinTargetId(undefined); setPinTargetName(undefined); }}
        staffId={pinTargetId} staffName={pinTargetName} />
    </div>
  );
}

// ─── Aba 2: Acesso por Usuário/Senha ─────────────────────────────────────────
function StaffByPassword() {
  const utils = trpc.useUtils();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: "", username: "", password: "", teamRole: "receptionist" as TeamRole,
    professionalId: null as number | null, email: "", phone: "",
  });

  const { data: members = [], isLoading } = trpc.team.list.useQuery();
  const { data: profList = [] } = trpc.professionals.listAdmin.useQuery();
  const { data: mySalon } = trpc.salon.getMySalon.useQuery();

  const createMutation = trpc.team.create.useMutation({
    onSuccess: () => { toast.success("Membro criado!"); utils.team.list.invalidate(); setShowForm(false); resetForm(); },
    onError: (err) => toast.error(err.message),
  });
  const updateMutation = trpc.team.update.useMutation({
    onSuccess: () => { toast.success("Membro atualizado!"); utils.team.list.invalidate(); setShowForm(false); setEditingId(null); resetForm(); },
    onError: (err) => toast.error(err.message),
  });
  const removeMutation = trpc.team.remove.useMutation({
    onSuccess: () => { toast.success("Membro removido!"); utils.team.list.invalidate(); setDeletingId(null); },
    onError: (err) => toast.error(err.message),
  });

  function resetForm() {
    setForm({ name: "", username: "", password: "", teamRole: "receptionist", professionalId: null, email: "", phone: "" });
  }
  function handleOpenCreate() {
    setEditingId(null); resetForm(); setShowPassword(false); setShowForm(true);
  }
  function handleOpenEdit(member: any) {
    setEditingId(member.id);
    setForm({ name: member.name || "", username: member.username || "", password: "", teamRole: member.teamRole || "receptionist",
      professionalId: member.professionalId || null, email: member.email || "", phone: member.phone || "" });
    setShowPassword(false); setShowForm(true);
  }
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.username.trim()) { toast.error("Nome e usuário são obrigatórios"); return; }
    if (!editingId && !form.password.trim()) { toast.error("Senha é obrigatória para novos membros"); return; }
    if (editingId) {
      updateMutation.mutate({ id: editingId, name: form.name, username: form.username,
        ...(form.password ? { password: form.password } : {}),
        teamRole: form.teamRole, professionalId: form.professionalId, email: form.email || null, phone: form.phone || null });
    } else {
      createMutation.mutate({ name: form.name, username: form.username, password: form.password,
        teamRole: form.teamRole, professionalId: form.professionalId, email: form.email || null, phone: form.phone || null });
    }
  }

  const loginUrl = `${window.location.origin}/team-login`;
  const copyLoginUrl = () => { navigator.clipboard.writeText(loginUrl); toast.success("Link copiado!"); };

  return (
    <div className="space-y-5">
      {/* Link de acesso */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-4 pb-4">
          <p className="text-sm font-medium text-amber-800 mb-2">Link de acesso da equipe:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-amber-200 rounded px-3 py-2 text-gray-700 truncate">{loginUrl}</code>
            <Button size="sm" variant="outline" onClick={copyLoginUrl} className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100">
              <Copy className="h-3.5 w-3.5 mr-1" />Copiar
            </Button>
          </div>
          {mySalon?.slug && (
            <p className="text-xs text-amber-600 mt-2">Identificador do salão: <strong>{mySalon.slug}</strong></p>
          )}
        </CardContent>
      </Card>

      {/* Legenda de roles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(Object.keys(TEAM_ROLE_LABELS) as TeamRole[]).map((role) => (
          <div key={role} className={`rounded-lg p-3 border ${TEAM_ROLE_COLORS[role].replace("text-", "border-").replace("700", "200")}`}>
            <p className={`text-xs font-bold ${TEAM_ROLE_COLORS[role].split(" ")[1]}`}>{TEAM_ROLE_LABELS[role]}</p>
            <p className="text-xs text-gray-500 mt-1">{TEAM_ROLE_DESCRIPTIONS[role]}</p>
          </div>
        ))}
      </div>

      {/* Botão adicionar */}
      <div className="flex justify-end">
        <Button onClick={handleOpenCreate} className="bg-amber-600 hover:bg-amber-700 text-white gap-2">
          <Plus className="h-4 w-4" />Novo Membro
        </Button>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : (members as any[]).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhum membro cadastrado</p>
            <p className="text-sm text-gray-400 mt-1">Clique em "Novo Membro" para começar</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {(members as any[]).map((member: any) => (
            <Card key={member.id} className="hover:shadow-md transition-shadow">
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                      <span className="text-amber-700 font-bold text-sm">{(member.name || "?").charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{member.name}</p>
                      <p className="text-xs text-gray-500">@{member.username}</p>
                      {(member.email || member.phone) && (
                        <p className="text-xs text-gray-400">{member.email}{member.email && member.phone ? " · " : ""}{member.phone}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`text-xs ${TEAM_ROLE_COLORS[member.teamRole as TeamRole] || "bg-gray-100 text-gray-600"}`}>
                      {TEAM_ROLE_LABELS[member.teamRole as TeamRole] || member.teamRole}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(member)} className="h-8 w-8 p-0">
                      <Pencil className="h-3.5 w-3.5 text-gray-500" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeletingId(member.id)} className="h-8 w-8 p-0">
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal criar/editar */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditingId(null); resetForm(); } }}>
        <DialogContent className="max-w-md max-h-[90dvh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>{editingId ? "Editar Membro" : "Novo Membro da Equipe"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto overscroll-contain space-y-4 pr-1">
              <div className="space-y-1.5">
                <Label>Nome completo *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Maria Silva" />
              </div>
              <div className="space-y-1.5">
                <Label>Usuário (login) *</Label>
                <Input value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "") })}
                  placeholder="maria.silva" />
                <p className="text-xs text-gray-400">Apenas letras minúsculas, números, ponto, hífen ou sublinhado</p>
              </div>
              <div className="space-y-1.5">
                <Label>{editingId ? "Nova senha (deixe em branco para manter)" : "Senha *"}</Label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={editingId ? "••••••••" : "mínimo 4 caracteres"} className="pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Nível de acesso *</Label>
                <Select value={form.teamRole} onValueChange={(v) => setForm({ ...form, teamRole: v as TeamRole })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(TEAM_ROLE_LABELS) as TeamRole[]).map((role) => (
                      <SelectItem key={role} value={role}>
                        <div>
                          <span className="font-medium">{TEAM_ROLE_LABELS[role]}</span>
                          <span className="text-xs text-gray-400 ml-2">— {TEAM_ROLE_DESCRIPTIONS[role]}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.teamRole === "professional" && (
                <div className="space-y-1.5">
                  <Label>Profissional vinculado</Label>
                  <Select value={form.professionalId?.toString() || "none"}
                    onValueChange={(v) => setForm({ ...form, professionalId: v === "none" ? null : parseInt(v) })}>
                    <SelectTrigger><SelectValue placeholder="Selecione o profissional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {(profList as any[]).map((p: any) => <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400">Vincula o acesso à agenda deste profissional</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>E-mail (opcional)</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="maria@email.com" />
                </div>
                <div className="space-y-1.5">
                  <Label>Telefone (opcional)</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(11) 99999-9999" />
                </div>
              </div>
            </div>
            <DialogFooter className="shrink-0 pt-4 border-t mt-4">
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); resetForm(); }}>Cancelar</Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={createMutation.isPending || updateMutation.isPending}>
                {createMutation.isPending || updateMutation.isPending ? "Salvando..." : editingId ? "Salvar" : "Criar Membro"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmação de remoção */}
      <AlertDialog open={!!deletingId} onOpenChange={(v) => { if (!v) setDeletingId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover membro?</AlertDialogTitle>
            <AlertDialogDescription>O membro perderá o acesso ao sistema. Pode ser revertido recriando o usuário.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => deletingId && removeMutation.mutate({ id: deletingId })}>
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Componente principal unificado ──────────────────────────────────────────
export default function AdminStaff() {
  const [activeTab, setActiveTab] = useState("email");
  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-5xl mx-auto space-y-4 md:space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
          <Users className="w-6 h-6 text-primary" />
          Equipe
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gerencie os membros da equipe, níveis de acesso e permissões.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Mobile: caixa suspensa */}
        <div className="block md:hidden mb-2">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione a seção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Acesso por E-mail</SelectItem>
              <SelectItem value="password">Acesso por Usuário/Senha</SelectItem>
              <SelectItem value="permissions">Permissões</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Desktop: abas normais */}
        <TabsList className="hidden md:inline-flex">
          <TabsTrigger value="email" className="gap-2">
            <Mail className="w-4 h-4" />
            Acesso por E-mail
          </TabsTrigger>
          <TabsTrigger value="password" className="gap-2">
            <KeyRound className="w-4 h-4" />
            Acesso por Usuário/Senha
          </TabsTrigger>
          <TabsTrigger value="permissions" className="gap-2">
            <ShieldPlus className="w-4 h-4" />
            Permissões
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="mt-5">
          <StaffByEmail />
        </TabsContent>

        <TabsContent value="password" className="mt-5">
          <StaffByPassword />
        </TabsContent>
        <TabsContent value="permissions" className="mt-5">
          <StaffPermissions />
        </TabsContent>
      </Tabs>
    </div>
  );
}
